import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { getProjectBySlug } from './projectsData';
import '../App.css';

const WorksProjectDetailPage = ({ nightMode, setNightMode }) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const project = getProjectBySlug(slug);

  const heroBackground = project?.heroBackground || '#ffffff';
  const heroBorderColor = project?.heroBorderColor || 'transparent';
  const infoBackground = project?.infoBackground || '#ffffff';
  const infoBorderColor = project?.infoBorderColor || heroBorderColor;

  // Get sections configuration
  const sections = project?.sections || {};

  return (
    <div className={`works-project-detail ${nightMode ? 'night' : ''}`}>
      <div className="works-nav">
        <motion.button
          className="works-back"
          onClick={() => navigate('/works/projects')}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -6 }}
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </motion.button>

        <motion.button
          className="works-theme"
          onClick={() => setNightMode(!nightMode)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {nightMode ? '☀' : '🌙'}
        </motion.button>
      </div>

      <motion.div
        className="project-hero-minimal"
        data-project={slug}
        style={{ background: heroBackground, '--project-hero-border': heroBorderColor }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div
          className="hero-minimal-label"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          CASE STUDY
        </motion.div>
        <motion.h1
          className="hero-minimal-title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          {project?.name || 'Coming Soon'}
        </motion.h1>
      </motion.div>

      <motion.div
        className="project-info-band"
        data-project={slug}
        style={{ '--project-info-bg': infoBackground, '--project-info-border': infoBorderColor }}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
      >
        <div className="project-info-grid">
          <div className="project-detail-cell">
            <span className="project-detail-heading">Industry</span>
            <span className="project-detail-text">{project?.industry || '—'}</span>
          </div>
          <div className="project-detail-cell">
            <span className="project-detail-heading">Year</span>
            <span className="project-detail-text">{project?.year || '—'}</span>
          </div>
          <div className="project-detail-cell services">
            <span className="project-detail-heading">Services</span>
            <div className="project-detail-chips">
              {(project?.services || ['Coming soon']).map((service) => (
                <span key={service} className="project-detail-chip">
                  {service}
                </span>
              ))}
            </div>
          </div>
          <div className="project-detail-cell info">
            <span className="project-detail-heading">Info</span>
            <p>{project?.info || 'This project will be documented shortly with full context, metrics, and outcomes.'}</p>
          </div>
        </div>

        {project?.quote && <div className="project-detail-quote">{project.quote}</div>}
      </motion.div>

      {/* Custom Images Section */}
      {sections.customImages?.map((image, index) => (
        <motion.div
          key={index}
          className="project-fullscreen-visual"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1 + (index * 0.04) }}
        >
          {image.caption && (
            <div className="project-fullscreen-visual-caption">{image.caption}</div>
          )}
          <img src={image.src} alt={image.alt} />
        </motion.div>
      ))}

      {/* Highlights Section */}
      {sections.highlights?.enabled && (
        <motion.div
          className={`project-highlights-section ${sections.highlights.scrollable ? 'mii-highlights' : ''}`}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.h2
            className="highlights-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            Highlights
          </motion.h2>

          <div className={`highlights-cards ${sections.highlights.scrollable ? 'mii-cards' : ''}`}>
            {sections.highlights.cards?.map((card, index) => (
              <motion.div
                key={index}
                className={`highlight-card ${card.align === 'right' ? 'text-right' : ''} ${card.small ? 'small-card' : ''}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: 0.3 + (index * 0.15), ease: [0.22, 1, 0.36, 1] }}
              >
                <h3 className="highlight-card-title">{card.title}</h3>
                <p className="highlight-card-text">{card.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Full Width Images Section */}
      {sections.fullWidthImages?.map((image, index) => (
        <motion.div
          key={index}
          className="project-fullscreen-visual no-padding"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <img src={image.src} alt={image.alt} />
        </motion.div>
      ))}

      {/* Moodboard Section */}
      {sections.moodboard?.enabled && (
        <motion.div
          className="project-moodboard-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.h2
            className="moodboard-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            Moodboard
          </motion.h2>
          <motion.div
            className="moodboard-image-wrapper"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            {sections.moodboard.type === 'video' ? (
              <video
                src={sections.moodboard.src}
                autoPlay
                loop
                muted
                playsInline
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            ) : (
              <img src={sections.moodboard.src} alt="Moodboard" style={{ width: '100%', height: 'auto', display: 'block' }} />
            )}
          </motion.div>
        </motion.div>
      )}

      {/* Promo/Campaign Section */}
      {sections.promo?.enabled && (
        <motion.div
          className="project-promo-section"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="promo-content-wrapper">
            <motion.div
              className="promo-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="promo-label">{sections.promo.label}</span>
              <h2 className="promo-title">{sections.promo.title}</h2>
              <p className="promo-subtitle">{sections.promo.subtitle}</p>
            </motion.div>

            <motion.div
              className="promo-video-container"
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="promo-video-wrapper">
                <video
                  src={sections.promo.videoSrc}
                  controls
                  playsInline
                  className="promo-video"
                />
              </div>
            </motion.div>

            {sections.promo.caption && (
              <motion.div
                className="promo-caption"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <p>{sections.promo.caption}</p>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}

      {/* Overview Section */}
      <motion.div
        className="project-overview-section"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.h2
          className="overview-title"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          Overview
        </motion.h2>

        {sections.overview && (
          <div className="overview-grid">
            <motion.div
              className="overview-column"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3 className="overview-column-title">CREDITS</h3>
              <p className="overview-column-text">{sections.overview.credits}</p>
            </motion.div>

            <motion.div
              className="overview-column"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3 className="overview-column-title">CAPABILITIES</h3>
              <ul className="overview-list">
                {sections.overview.capabilities?.map((capability, index) => (
                  <li key={index}>{capability}</li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="overview-column"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <h3 className="overview-column-title">DELIVERABLES</h3>
              <div className="deliverables-list">
                {sections.overview.deliverables?.map((deliverable, index) => (
                  <div key={index} className="deliverable-item">{deliverable}</div>
                ))}
              </div>
            </motion.div>
          </div>
        )}

        <motion.div
          className="overview-download"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <a href="#" className="download-narrative-btn">
            <span>DOWNLOAD NARRATIVE</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v8m0 0l-4-4m4 4l4-4" />
            </svg>
          </a>
        </motion.div>
      </motion.div>

      <Navigation activeSection="work" onNavigate={() => {}} nightMode={nightMode} />
    </div>
  );
};

export default WorksProjectDetailPage;
